import React from 'react';
import { Link } from 'react-router-dom';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { GridView } from 'components/cosmos/gridView';
import Loading from 'components/cosmos/loading';
import { Tag } from 'components/cosmos/tag';
import { I18n, translate } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import ClubDate, { FORMATS } from 'helpers/ClubDate';
import flowRight from 'lodash/flowRight';
import { inject, PropTypes as MobxPropTypes } from 'mobx-react';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import ClassListSearch from 'modules/classManager/classListView/ClassListSearch';
import ClassListSort from 'modules/classManager/classListView/ClassListSort';
import { ClassListViewActions } from 'modules/classManager/classListView/classListViewActions';
import { ClassListViewCreateButton } from 'modules/classManager/classListView/classListViewCreateButton';
import { ClassListViewDescription } from 'modules/classManager/classListView/classListViewDescription';
import ClassListViewEmptyState from 'modules/classManager/classListView/ClassListViewEmptyState';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import PropTypes from 'prop-types';

const ClassListViewMobile = ({
  classes,
  isLoading = true,
  hasSearch = false,
  t
}) => {
  const totalClasses = classes.length;

  const handleEdit = (classId) => {
    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CLASS.EDIT_LIST_CLASS,
        action: EVENTS_INTERACTIONS.CLICK,
        label: `${EVENTS_LABELS.CLASS.CLASS_ID}=${classId}`
      })
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return <Loading center data-test="loader" />;
    }

    if (hasSearch && totalClasses === 0) {
      return <ClassListViewEmptyState />;
    }

    return classes.map((classItem, index) => {
      const isLastItem = totalClasses === index + 1;
      const classes = classnames(
        { '_border-bottom': !isLastItem },
        '_px-0 _py-5 _mb-4'
      );

      return (
        <GridView key={classItem.id} className={classes}>
          <GridView.Row className="_align-items-center">
            <GridView.Col size={10} className="_text-3 _font-weight-bold">
              <Link
                to={ROUTES.CLASS.EDIT.replace(':id', classItem.id)}
                className="_font-weight-bold _text-gray-700 _d-block"
                onClick={() => handleEdit(classItem.id)}
              >
                {classItem.name}
              </Link>
            </GridView.Col>

            <GridView.Col size={2} className="_text-right">
              <ClassListViewActions classItem={classItem} />
            </GridView.Col>
          </GridView.Row>

          <GridView.Row className="_mt-1">
            <GridView.Col size={12}>
              <p className="_font-weight-bold _text-gray-600">
                <I18n
                  label="class_manager.class_list.total_member"
                  data={{ count: classItem.totalUsers }}
                />
              </p>

              <p className="_text-gray-600">
                <I18n
                  label="general.created_in"
                  data={{
                    date: ClubDate.resolve(classItem.createDate).format(
                      FORMATS.DDMMYYYY
                    )
                  }}
                />
              </p>

              {classItem.defaultClass && (
                <Tag variation="blue" size="sm">
                  <I18n label="general.default_class_label" checkOnlineEvent />
                  <FontAwesomeIcon icon={faCircleInfo} className="_ml-1" />
                </Tag>
              )}
            </GridView.Col>
          </GridView.Row>
        </GridView>
      );
    });
  };

  return (
    <ContentBasePage className="content__grid" data-test="mobile-class-view">
      <ContentBasePage.Header
        title={t('class_manager.class_list.title', { checkOnlineEvent: true })}
        isTitleLabel={false}
        hideBackHandler
      />
      <ClassListViewDescription className="_mb-5" />
      <ClassListViewCreateButton className="_mb-5" />

      <ClassListSort className="_mb-4 _w-full" />
      <ClassListSearch className="_mb-4" />

      {renderContent()}
    </ContentBasePage>
  );
};

ClassListViewMobile.propTypes = {
  classes: MobxPropTypes.arrayOrObservableArray,
  isLoading: PropTypes.bool,
  hasSearch: PropTypes.bool
};

export default flowRight(
  inject(({ ClassStore }) => ({
    classes: ClassStore.classes,
    isLoading: ClassStore.isFetchingFormattedClasses,
    hasSearch: ClassStore.search.length > 0
  })),
  translate()
)(ClassListViewMobile);
